// The butterfly knife: "CSGO-Styled Butterfly Knife" by Aslady (sketchfab.com/Aslady, CC-BY-4.0),
// split and simplified by scripts/import-butterfly.mjs. The blade takes the game's finishes (projected
// onto its flat side, like the karambit), the handles stay black, the inserts follow the finish.
//
// It's placed in the knife arm's coordinates (karambitModel.ts): +X runs along the handle from the
// index finger toward the pinky, so the blade comes out of the fist on the thumb side. The blade and
// the second handle sit in a "swing" group turning on the first handle's pin, for the inspect flip.

import * as THREE from 'three';
import type { KnifeModel } from './karambitModel';
import { projectBladeUvs } from './bladeUvs';

export const BUTTERFLY_URL = `${import.meta.env.BASE_URL ?? '/'}assets/models/butterfly.glb`;

/** Model units -> knife arm units (only the proportions matter; the arm scales the knife to the fist). */
const FIT_SCALE = 0.4;
/** Where the index finger sits: this far down the handle from the pivot pins (model units). */
const INDEX_BELOW_PIVOT = 0.55;
/** How much of the handle the fist covers, from the index finger (model units). */
const FIST_LENGTH = 4.3;
/** Turns the blade's edge toward the knuckles (+1) or the wrist (-1). */
const EDGE_SIDE = 1;

let loading: Promise<KnifeModel | null> | null = null;

/** Load once. `blade` is the finish material (shared with the karambit). */
export function loadButterfly(blade: THREE.Material): Promise<KnifeModel | null> {
  loading ??= (async () => {
    try {
      const [{ GLTFLoader }, { toCreasedNormals }] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/utils/BufferGeometryUtils.js'),
      ]);
      const gltf = await new GLTFLoader().loadAsync(BUTTERFLY_URL);
      const pivots = (gltf.asset.extras as { pivots?: Record<'A' | 'B', number[]> } | undefined)?.pivots;
      if (!pivots) return null;
      const steel = new THREE.MeshPhongMaterial({ color: 0xc9ced6, specular: 0xffffff, shininess: 120 });
      const handle = new THREE.MeshPhongMaterial({ color: 0x0c0d10, specular: 0x30343c, shininess: 30 });
      const accent = new THREE.MeshPhongMaterial({ color: 0x1b2f7a, specular: 0x6070a0, shininess: 40 });
      const material = (part: string) =>
        part === 'blade'
          ? blade
          : part.startsWith('insert')
            ? accent
            : part.startsWith('handle')
              ? handle
              : steel;

      // Model frame: handle axis from the middle of the pins to the latch at the bottom end.
      const pinA = new THREE.Vector3().fromArray(pivots.A);
      const pinB = new THREE.Vector3().fromArray(pivots.B);
      const pins = pinA.clone().add(pinB).multiplyScalar(0.5);
      pins.z = 0;
      gltf.scene.updateMatrixWorld(true);
      const meshes: THREE.Mesh[] = [];
      gltf.scene.traverse((o) => {
        if (o instanceof THREE.Mesh) meshes.push(o);
      });
      const byName = new Map(meshes.map((m) => [m.name.replace(/_\d+$/, ''), m]));
      // The bottom end of the closed handles (the latch corner of handle A).
      const box = new THREE.Box3().setFromObject(byName.get('handleA')!);
      const butt = new THREE.Vector3(box.max.x, box.min.y, 0);
      const ax = butt.clone().sub(pins);
      ax.z = 0;
      ax.normalize();
      const ay = new THREE.Vector3(-ax.y, ax.x, 0).multiplyScalar(EDGE_SIDE);
      const az = new THREE.Vector3().crossVectors(ax, ay);
      const index = pins.clone().addScaledVector(ax, INDEX_BELOW_PIVOT);

      // Inner: model coordinates. Swing: the blade and handle B, turning on handle A's pin (model Z).
      const inner = new THREE.Group();
      const swing = new THREE.Group();
      swing.name = 'swing';
      swing.position.set(pinA.x, pinA.y, 0);
      inner.add(swing);
      for (const m of meshes) {
        const part = m.name.replace(/_\d+$/, '');
        const geo = toCreasedNormals(m.geometry.clone().applyMatrix4(m.matrixWorld), (35 * Math.PI) / 180);
        // Base (at the pins) -> tip runs against the handle axis.
        if (part === 'blade') projectBladeUvs(geo, pins, ax.clone().negate(), ay);
        const mesh = new THREE.Mesh(geo, material(part));
        mesh.name = part;
        mesh.frustumCulled = false;
        if (part === 'blade' || part === 'bladePins' || part.endsWith('B')) {
          mesh.position.set(-pinA.x, -pinA.y, 0);
          swing.add(mesh);
        } else inner.add(mesh);
      }
      // Model -> knife arm coordinates: the axes above become X, Y, Z, the index point the origin.
      const basis = new THREE.Matrix4().makeBasis(ax, ay, az).transpose();
      inner.applyMatrix4(
        new THREE.Matrix4()
          .makeScale(FIT_SCALE, FIT_SCALE, FIT_SCALE)
          .multiply(basis)
          .multiply(new THREE.Matrix4().makeTranslation(-index.x, -index.y, -index.z)),
      );
      const object = new THREE.Group();
      object.add(inner);
      // How the arm holds it (fpArms.ts): the index finger at the origin, the fist along +X.
      object.userData.grip = { index: new THREE.Vector3(0, 0, 0), handle: FIST_LENGTH * FIT_SCALE };
      return { object, setAccent: (c: string) => accent.color.set(c) };
    } catch {
      return null;
    }
  })();
  return loading;
}
